import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Box,
} from '@mui/material';
import { registrationService } from '../../services/registrationService';
import paymentServiceMercadoPago from '../../services/mercadoPagoService';
import CheckoutPaymentRouter, { type CheckoutTab } from './components/CheckoutPaymentRouter';
import PixCopyCodeField from './components/PixCopyCodeField';
import type { PaymentProviderRoutesDto } from '../../types/checkoutPayment';
import { parseErrorMessage } from '../../utils/errorHelpers';
import { CHECKOUT_PUBLIC_ERROR_MESSAGE } from '../../utils/checkoutPublicError';
import { useBrandRegistrationCopy } from '../../brands/registrationCopy';

const toErrorMessage = (_error: unknown): string => CHECKOUT_PUBLIC_ERROR_MESSAGE;

const getPixFailureMessage = (_data?: any): string => CHECKOUT_PUBLIC_ERROR_MESSAGE;

const PendingPaymentPage: React.FC = () => {
  const t = useBrandRegistrationCopy();
  const { registrationId } = useParams<{ registrationId: string }>();

  const [loading, setLoading] = useState(true);
  const [registration, setRegistration] = useState<any>(null);
  const [error, setError] = useState('');
  const [paymentError, setPaymentError] = useState('');
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [pixQRCodeBase64, setPixQRCodeBase64] = useState<string | null>(null);
  const [pixCode, setPixCode] = useState<string | null>(null);
  const [pixPaymentPending, setPixPaymentPending] = useState(false);
  const [cardPaymentPending, setCardPaymentPending] = useState(false);
  const [checkoutPaymentTab, setCheckoutPaymentTab] = useState<CheckoutTab>('pix');
  const [pagarmePixBusy, setPagarmePixBusy] = useState(false);
  const [paymentProviderRoutes, setPaymentProviderRoutes] = useState<PaymentProviderRoutesDto>({
    pix: 'pagarme',
    credit_card: 'mercadopago',
    debit_card: 'mercadopago',
  });
  const [platformInstallmentSettings, setPlatformInstallmentSettings] = useState({
    enabled: true,
    maxInstallments: 12,
  });

  useEffect(() => {
    const loadInst = async () => {
      try {
        const inst = await paymentServiceMercadoPago.getInstallmentSettings();
        setPlatformInstallmentSettings({
          enabled: inst.enabled !== false,
          maxInstallments: Math.max(1, Math.min(Number(inst.maxInstallments) || 12, 12)),
        });
      } catch {
        /* defaults */
      }
    };
    loadInst();
  }, []);

  useEffect(() => {
    if (!registrationId) return;
    const loadRoutes = async () => {
      try {
        const routes = await paymentServiceMercadoPago.getPaymentProviderRoutes({ registrationId });
        setPaymentProviderRoutes(routes);
      } catch {
        /* mantém defaults */
      }
    };
    loadRoutes();
  }, [registrationId]);

  // Carrega os dados da inscrição
  useEffect(() => {
    const fetchRegistration = async () => {
      try {
        const res = await registrationService.getById(registrationId!);
        if (res.success) {
          setRegistration(res.data);
        } else {
          setError(res.message || t('Erro ao buscar inscrição.'));
        }
      } catch {
        setError('Erro inesperado ao carregar os dados.');
      } finally {
        setLoading(false);
      }
    };

    fetchRegistration();
  }, [registrationId]);

  const { formData, eventId, ticketId, amountPaid, paymentStatus } = registration || {};
  const participantName = formData?.name || '';
  const eventTitle = eventId?.title || '';
  const ticketName = ticketId?.name || '';

  // Polling para verificar se foi pago
  useEffect(() => {
    if ((!pixPaymentPending && !cardPaymentPending) || !registrationId) return;

    let isActive = true;
    let attempt = 0;
    let delay = 5000;
    const maxAttempts = 6;

    const pollStatus = async () => {
      if (!isActive || attempt >= maxAttempts) return;
      try {
        const result = await registrationService.checkStatus(registrationId);
        if (result.success && result.data.paymentStatus === 'paid') {
          setPaymentSuccess(true);
          setPixPaymentPending(false);
          setCardPaymentPending(false);
          return;
        }
        attempt += 1;
        delay = Math.min(delay * 2, 160000);
        if (isActive) {
          setTimeout(pollStatus, delay);
        }
      } catch {
        attempt += 1;
        delay = Math.min(delay * 2, 160000);
        if (isActive) {
          setTimeout(pollStatus, delay);
        }
      }
    };

    pollStatus();

    return () => {
      isActive = false;
    };
  }, [pixPaymentPending, cardPaymentPending, registrationId]);

  const handlePagarmePixPendingRegistration = async (cpfDigits: string) => {
    if (!registrationId || amountPaid == null) {
      setPaymentError(t('Dados da inscrição não carregados.'));
      return;
    }
    try {
      setPaymentError('');
      setPagarmePixBusy(true);
      const res = await paymentServiceMercadoPago.generatePaymentByRegistration(registrationId!, {
        paymentMethodId: 'pix',
        installments: 1,
        token: '',
        customerCPF: cpfDigits,
        transactionAmount: amountPaid,
        customerEmail: formData?.email,
        customerName: formData?.name,
        customerPhone: formData?.phone,
        external_reference: registrationId!,
      });

      if (res.success && res.data?.payment_method_id === 'pix') {
        if (String(res.data?.status || '').toLowerCase() === 'failed') {
          setPaymentError(getPixFailureMessage(res.data));
          return;
        }
        const transactionData = res.data.point_of_interaction?.transaction_data;
        const qr = transactionData?.qr_code;
        if (qr) {
          let b64 = transactionData?.qr_code_base64 as string | undefined;
          if (typeof b64 === 'string' && b64.startsWith('data:image')) {
            b64 = b64.split(',')[1];
          }
          setPixQRCodeBase64(b64 || null);
          setPixCode(qr);
          setPixPaymentPending(true);
          return;
        }
      }

      setPaymentError(CHECKOUT_PUBLIC_ERROR_MESSAGE);
    } catch {
      setPaymentError(CHECKOUT_PUBLIC_ERROR_MESSAGE);
    } finally {
      setPagarmePixBusy(false);
    }
  };

  const handlePayment = async (
    cardTokenId: string,
    paymentMethodId: string,
    issuerId: string | null,
    installments: number,
    payer: any,
    installmentTotalAmount?: number
  ) => {
    try {
      setPaymentError('');

    const finalTransactionAmount =
      typeof installmentTotalAmount === 'number' && installmentTotalAmount > amountPaid
        ? installmentTotalAmount
        : amountPaid;

    const res = await paymentServiceMercadoPago.generatePaymentByRegistration(
      registrationId!,
      {
        paymentMethodId: paymentMethodId,
        token: cardTokenId,
        issuer_id: issuerId || undefined,
        installments,
        transactionAmount: finalTransactionAmount,
        customerEmail: formData?.email,
        customerName: formData?.name,
        customerCPF: payer?.identification?.number || '',
        external_reference: registrationId!,
      }
    );

    if (res.success) {
      const { data } = res;

      if (data.payment_method_id === 'pix') {
        if (String(data?.status || '').toLowerCase() === 'failed') {
          setPaymentError(getPixFailureMessage(data));
          return;
        }
        const transactionData = data.point_of_interaction?.transaction_data;
        const qr = transactionData?.qr_code;
        if (qr) {
          let b64 = transactionData?.qr_code_base64 as string | undefined;
          if (typeof b64 === 'string' && b64.startsWith('data:image')) {
            b64 = b64.split(',')[1];
          }
          setPixQRCodeBase64(b64 || null);
          setPixCode(qr);
          setPixPaymentPending(true);
          return;
        }
      }

      // Cartão: não mostrar sucesso aqui — aguardar confirmação real (polling → paymentStatus === 'paid')
      const terminalFailureStatuses = [
        'rejected',
        'cancelled',
        'refunded',
        'charged_back',
        'failed',
        'canceled',
      ];
      if (terminalFailureStatuses.includes(data.status)) {
        const detail = (data as { status_detail?: string }).status_detail || data.status;
        setPaymentError(
          `Pagamento não concluído${detail ? ` (${detail})` : ''}. Verifique o cartão ou tente outro método.`
        );
        return;
      }

      setCardPaymentPending(true);
      return;
    }

    setPaymentError(CHECKOUT_PUBLIC_ERROR_MESSAGE);
  } catch {
    setPaymentError(CHECKOUT_PUBLIC_ERROR_MESSAGE);
  }
};

  if (loading) {
    return (
      <Container sx={{ mt: 5 }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ mt: 5 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!registration) return null;
  
  return (
    <Container maxWidth="sm" sx={{ mt: 5 }}>
      <Paper sx={{ p: 4 }}>
        <Typography variant="h5" gutterBottom>
          Pagamento Pendente
        </Typography>

        <Typography gutterBottom>
          Olá <strong>{participantName}</strong>, {t('finalize o pagamento da sua inscrição no evento:')}
        </Typography>

        <Typography variant="h6" sx={{ mt: 1 }}>
          {eventTitle}
        </Typography>
        <Typography>
          Ingresso selecionado: <strong>{ticketName}</strong>
        </Typography>
        <Typography sx={{ mb: 2 }}>
          Valor total: <strong>R$ {amountPaid?.toFixed(2)}</strong>
        </Typography>

        {paymentStatus === 'paid' || paymentSuccess ? (
          <Alert severity="success">Pagamento confirmado com sucesso!</Alert>
        ) : cardPaymentPending && !pixPaymentPending ? (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <CircularProgress sx={{ mb: 2 }} />
            <Typography variant="h6" fontWeight={700} gutterBottom>
              Processando seu pagamento
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              Seu pagamento com cartão está sendo confirmado. Isso pode levar alguns instantes.
            </Typography>
            <Alert severity="info">
              Você verá a mensagem de confirmação aqui quando o pagamento for aprovado de fato. Você também receberá um e-mail.
            </Alert>
          </Box>
        ) : pixPaymentPending && pixCode ? (
          <Box textAlign="center" sx={{ mt: 2 }}>
            <Typography variant="subtitle1">Pagamento via Pix</Typography>
            {pixQRCodeBase64 ? (
              <img
                src={`data:image/png;base64,${pixQRCodeBase64}`}
                alt="QR Code Pix"
                width={200}
              />
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Copie o código abaixo no app do seu banco.
              </Typography>
            )}
            <PixCopyCodeField code={pixCode} />
            <Alert severity="info" sx={{ mt: 2 }}>
              Após o pagamento, aguardamos a confirmação. Você receberá um e-mail assim que for processado.
            </Alert>
          </Box>
        ) : (
          <Box>
            <CheckoutPaymentRouter
              amount={amountPaid}
              payerEmail={formData?.email}
              routes={paymentProviderRoutes}
              checkoutTab={checkoutPaymentTab}
              onCheckoutTabChange={setCheckoutPaymentTab}
              onCardTokenReceived={handlePayment}
              onError={(msg) => setPaymentError(toErrorMessage(msg))}
              allowInstallments={platformInstallmentSettings.enabled}
              maxInstallments={platformInstallmentSettings.maxInstallments}
              customerName={participantName}
              customerCPF={(formData?.cardHolderCPF || '').replace(/\D/g, '')}
              onRequestPagarmePix={handlePagarmePixPendingRegistration}
              pagarmePixBusy={pagarmePixBusy}
            />
            {paymentError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {parseErrorMessage(paymentError)}
              </Alert>
            )}
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default PendingPaymentPage;
